import config from '../utils/envLoader.js';

class ApiService {
    constructor() {
        this.baseURL = config.apiBaseUrl;
        this.access_tokenKey = config.tokenStorageKey;
        this.access_token = localStorage.getItem(this.access_tokenKey);
        this.timeout = config.apiTimeout;

        if (config.debug.general) {
            console.log('=== ApiService Constructor Debug ===');
            console.log('Environment variables from config:');
            console.log('  API Base URL:', config.apiBaseUrl);
            console.log('  Token Storage Key:', config.tokenStorageKey);
            console.log('  API Timeout:', config.apiTimeout);
            console.log('  Environment:', config.environment);
            console.log('Resolved configuration:');
            console.log('  baseURL:', this.baseURL);
            console.log('  tokenKey:', this.access_tokenKey);
            console.log('  timeout:', this.timeout);
            console.log('Token found in localStorage:', !!this.access_token);
        }
        if (this.access_token) {
            //console.log('  Token preview:', this.access_token.substring(0, 20) + '...');
        }
        if (config.debug.general) console.log('=== End Constructor Debug ===');
    }

    // Helper method to create base request configuration
    _createRequestConfig(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;

        // Default headers
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        // Add auth token if available
        if (this.access_token) {
            headers.Authorization = `Bearer ${this.access_token}`;
        }

        return {
            url,
            config: {
                ...options,
                headers
            }
        };
    }

    // Helper method to handle response processing
    async _processResponse(response, method, url) {
        // console.log(`=== ${method} Response Debug ===`);
        // console.log('Fetch response received. Status:', response.status, 'StatusText:', response.statusText);
        // console.log('Response headers:', Object.fromEntries(response.headers.entries()));

        // Handle 401 unauthorized
        if (response.status === 401) {
            console.log(response);
            console.warn('Received 401 unauthorized, logging out user');
            this.logoutLocal(); // Use local logout for auth failures
            //window.location.reload();
            throw new Error('Unauthorized');
        }
        // Handle 403 forbidden
        if (response.status === 403) {
            console.warn('Received 403 forbidden, logging out user');
            console.log(response);
            this.logoutLocal(); // Use local logout for auth failures
            window.location.reload();
            throw new Error('Forbidden');
        }

        // Check if response is ok
        if (!response.ok) {
            console.error('Response not ok. Status:', response.status, 'StatusText:', response.statusText);
            let errorData;
            try {
                errorData = await response.json();
                console.log('Error response data:', errorData);
            } catch (parseError) {
                console.warn('Failed to parse error response as JSON:', parseError.message);
                errorData = {};
            }
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error('Throwing error:', errorMessage);
            throw new Error(errorMessage);
        }

        //console.log('Response ok, parsing JSON...');
        const data = await response.json();
        //console.log('Response data parsed successfully:', data);
        //console.log(`=== End ${method} Response Debug ===`);
        return data;
    }

    // GET request handler
    async get(endpoint, options = {}) {
        const useTimeout = options.useTimeout !== undefined ? options.useTimeout : false;
        const { url, config } = this._createRequestConfig(endpoint, {
            method: 'GET',
            ...options
        });
        //console.log('=== GET Request Debug ===');
        //console.log('Making GET request to:', url);
        //console.log('Request config:', JSON.stringify(config, null, 2));

        let controller, timeoutId;
        if (useTimeout) {
            controller = new AbortController();
            timeoutId = setTimeout(() => {
                controller.abort();
            }, this.timeout);
            config.signal = controller.signal;
        }

        try {
            //console.log('Sending GET fetch request...');
            const response = await fetch(url, config);
            if (useTimeout) clearTimeout(timeoutId);
            return await this._processResponse(response, 'GET', url);
        } catch (error) {
            if (useTimeout) clearTimeout(timeoutId);
            console.log('=== GET Request Error ===');
            console.error('Error caught in GET request:', error);
            console.log('=== End GET Request Error ===');
            throw error;
        }
    }

    // POST request handler
    async post(endpoint, data = null, options = {}) {
        const useTimeout = options.useTimeout !== undefined ? options.useTimeout : true;
        const { url, config } = this._createRequestConfig(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : null,
            ...options
        });
        //console.log('=== POST Request Debug ===');
        //console.log('Making POST request to:', url);
        //console.log('Request data:', data);
        //console.log('Request config:', JSON.stringify(config, null, 2));

        let controller, timeoutId;
        if (useTimeout) {
            controller = new AbortController();
            timeoutId = setTimeout(() => {
                controller.abort();
            }, this.timeout);
            config.signal = controller.signal;
        }

        try {
            //console.log('Sending POST fetch request...');
            const response = await fetch(url, config);
            if (useTimeout) clearTimeout(timeoutId);
            return await this._processResponse(response, 'POST', url);
        } catch (error) {
            if (useTimeout) clearTimeout(timeoutId);
            console.log('=== POST Request Error ===');
            console.error('Error caught in POST request:', error);
            console.log('=== End POST Request Error ===');
            throw error;
        }
    }

    // PUT request handler
    async put(endpoint, data = null, options = {}) {
        const useTimeout = options.useTimeout !== undefined ? options.useTimeout : true;
        const { url, config } = this._createRequestConfig(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : null,
            ...options
        });
        //console.log('=== PUT Request Debug ===');
        //console.log('Making PUT request to:', url);
        //console.log('Request data:', data);
        //console.log('Request config:', JSON.stringify(config, null, 2));

        let controller, timeoutId;
        if (useTimeout) {
            controller = new AbortController();
            timeoutId = setTimeout(() => {
                controller.abort();
            }, this.timeout);
            config.signal = controller.signal;
        }

        try {
            //console.log('Sending PUT fetch request...');
            const response = await fetch(url, config);
            if (useTimeout) clearTimeout(timeoutId);
            return await this._processResponse(response, 'PUT', url);
        } catch (error) {
            if (useTimeout) clearTimeout(timeoutId);
            console.log('=== PUT Request Error ===');
            console.error('Error caught in PUT request:', error);
            console.log('=== End PUT Request Error ===');
            throw error;
        }
    }

    // DELETE request handler
    async delete(endpoint, options = {}) {
        const useTimeout = options.useTimeout !== undefined ? options.useTimeout : true;
        const { url, config } = this._createRequestConfig(endpoint, {
            method: 'DELETE',
            ...options
        });
        //console.log('=== DELETE Request Debug ===');
        //console.log('Making DELETE request to:', url);
        //console.log('Request config:', JSON.stringify(config, null, 2));

        let controller, timeoutId;
        if (useTimeout) {
            controller = new AbortController();
            timeoutId = setTimeout(() => {
                controller.abort();
            }, this.timeout);
            config.signal = controller.signal;
        }

        try {
            //console.log('Sending DELETE fetch request...');
            const response = await fetch(url, config);
            if (useTimeout) clearTimeout(timeoutId);
            return await this._processResponse(response, 'DELETE', url);
        } catch (error) {
            if (useTimeout) clearTimeout(timeoutId);
            console.log('=== DELETE Request Error ===');
            console.error('Error caught in DELETE request:', error);
            console.log('=== End DELETE Request Error ===');
            throw error;
        }
    }

    // Legacy makeRequest method for backward compatibility
    async makeRequest(endpoint, options = {}) {
        const method = (options.method || 'GET').toUpperCase();

        switch (method) {
            case 'GET':
                return this.get(endpoint, options);
            case 'POST':
                // Extract body from options and pass as data parameter
                const postData = options.body ? JSON.parse(options.body) : null;
                const postOptions = { ...options };
                delete postOptions.body;
                delete postOptions.method;
                return this.post(endpoint, postData, postOptions);
            case 'PUT':
                // Extract body from options and pass as data parameter
                const putData = options.body ? JSON.parse(options.body) : null;
                const putOptions = { ...options };
                delete putOptions.body;
                delete putOptions.method;
                return this.put(endpoint, putData, putOptions);
            case 'DELETE':
                const deleteOptions = { ...options };
                delete deleteOptions.method;
                return this.delete(endpoint, deleteOptions);
            default:
                throw new Error(`Unsupported HTTP method: ${method}`);
        }
    }

    // Authentication APIs
    async login(email, password) {
        //console.log('=== LOGIN ATTEMPT DEBUG ===');
        //console.log('Attempting login for email:', email);
        //console.log('Password provided:', !!password);
        //console.log('Password length:', password ? password.length : 0);
        //console.log('Current token before login:', this.access_token ? 'exists' : 'null');

        try {
            //console.log('Calling POST request for login...');
            const response = await this.post('/enterprise/auth/login', {
                email,
                password,
                app_version: process.env.REACT_APP_VERSION,
                build_number: process.env.REACT_APP_BUILD_NUMBER
            });

            //console.log('Login response received:', response);
            //console.log('Response type:', typeof response);
            //console.log('Response keys:', Object.keys(response || {}));

            // Store token and user info
            if (response.data.access_token) {
                //console.log('Token found in response, storing...');
                //console.log('Token preview:', response.data.access_token.substring(0, 20) + '...');
                //console.log('User data:', response.data.user);

                this.access_token = response.data.access_token;
                //console.log('Setting token in instance:', this.access_token ? 'success' : 'failed');

                localStorage.setItem(this.access_tokenKey, this.access_token);
                //console.log('Token stored in localStorage with key:', this.access_tokenKey);

                localStorage.setItem('user', JSON.stringify(response.data.user));
                //console.log('User data stored in localStorage');

                // Verify storage
                const storedToken = localStorage.getItem(this.access_tokenKey);
                const storedUser = localStorage.getItem('user');
                //console.log('Verification - stored token exists:', !!storedToken);
                //console.log('Verification - stored user exists:', !!storedUser);

                //console.log('Login successful - preparing success response');
                const successResponse = { success: true, user: response.data.user, token: response.data.access_token };
                //console.log('Success response:', successResponse);
                //console.log('=== END LOGIN SUCCESS DEBUG ===');
                return successResponse;
            }

            //console.error('Login failed - no token in response');
            //console.error('Full response:', response);
            // If no token returned, login failed
            const failureResponse = { success: false, error: 'Invalid credentials' };
            //console.log('Failure response:', failureResponse);
            //console.log('=== END LOGIN FAILURE DEBUG ===');
            return failureResponse;
        } catch (error) {
            console.error('=== LOGIN ERROR DEBUG ===');
            console.error('Login error caught:', error);
            console.error('Error type:', error.constructor.name);
            console.error('Error message:', error.message);
            console.error('Error stack:', error.stack);
            const errorResponse = { success: false, error: error.message };
            console.log('Error response:', errorResponse);
            console.log('=== END LOGIN ERROR DEBUG ===');
            return errorResponse;
        }
    }

    async logout() {
        //console.log('=== LOGOUT DEBUG ===');
        //console.log('Current token before logout:', this.access_token ? 'exists' : 'null');
        //console.log('TokenKey:', this.access_tokenKey);

        // Call server logout endpoint if token exists
        if (this.access_token) {
            try {
                //console.log('Calling server logout endpoint...');
                await this.post('/enterprise/logout');
                //console.log('Server logout completed successfully');
            } catch (error) {
                console.warn('Error calling server logout endpoint:', error.message);
                //Continue with local logout even if server call fails
            }
        }

        this.logoutLocal();
    }

    logoutLocal() {
        // console.log('=== LOCAL LOGOUT DEBUG ===');
        // console.log('Current token before logout:', this.access_token ? 'exists' : 'null');
        // console.log('TokenKey:', this.access_tokenKey);

        this.access_token = null;
        // console.log('Token set to null in instance');

        localStorage.removeItem(this.access_tokenKey);
        //console.log('Token removed from localStorage');

        localStorage.removeItem('user');
        //console.log('User data removed from localStorage');

        // Verify removal
        const tokenCheck = localStorage.getItem(this.access_tokenKey);
        const userCheck = localStorage.getItem('user');
        //console.log('Verification - token in localStorage:', tokenCheck);
        //console.log('Verification - user in localStorage:', userCheck);

        //console.log('Local logout completed successfully');
        //console.log('=== END LOCAL LOGOUT DEBUG ===');
    }

    isAuthenticated() {
        if (config.debug.auth) {
            console.log('=== AUTHENTICATION CHECK DEBUG ===');
            console.log('Current token in instance:', this.access_token ? 'exists' : 'null');
        }
        if (this.access_token) {
            //console.log('Token preview:', this.access_token.substring(0, 20) + '...');
        }

        const tokenInStorage = localStorage.getItem(this.access_tokenKey);
        if (config.debug.auth) {
            console.log('Token in localStorage:', tokenInStorage ? 'exists' : 'null');
        }

        const authenticated = !!this.access_token;
        if (config.debug.auth) {
            console.log('Authentication result:', authenticated);
            console.log('=== END AUTHENTICATION CHECK DEBUG ===');
        }
        return authenticated;
    }

    // Returns user data stored in localStorage (synchronous)
    getStoredPlatformUser() {
        const userString = localStorage.getItem('user');
        if (config.debug.auth) {
            //console.log('=== GET STORED USER DEBUG ===');
            //console.log('User string from localStorage:', userString);
        }

        let user = null;
        if (userString) {
            try {
                user = JSON.parse(userString);
                //console.log('Parsed stored user object:', user);
            } catch (error) {
                console.error('Error parsing user from localStorage:', error);
            }
        }

        //console.log('Returning stored user:', user);
        //console.log('=== END GET STORED USER DEBUG ===');
        return user;
    }

    // Dashboard APIs
    async getDashboardStats() {
        //console.log('Fetching dashboard stats');

        try {
            const response = await this.get('/enterprise/dashboard/metrics');
            //console.log('Dashboard stats fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            return { success: false, error: error.message };
        }
    }

    async getRecentActivity() {
        // console.log('Fetching recent activity');

        try {
            const response = await this.get('/enterprise/dashboard/activity');
            // console.log('Recent activity fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching recent activity:', error);
            return { success: false, error: error.message };
        }
    }

    // Organization APIs
    async getOrganizations(page = 1, limit = 10, search = '') {
        console.log('Fetching organizations:', { page, limit, search });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search })
            });

            const response = await this.get(`/organization/`);
            // console.log('Organizations fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching organizations:', error);
            return { success: false, error: error.message };
        }
    }

    async getOrganization(id) {
        console.log('Fetching organization with ID:', id);

        try {
            const response = await this.get(`/organization/${id}`);
            console.log('Organization fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching organization:', error);
            return { success: false, error: error.message };
        }
    }

    async checkOrganizationCodeExists(code) {
        console.log('Checking if organization code exists:', code);

        try {
            const response = await this.get(`/organization/check-code/${code}`);
            console.log('Organization code check completed');
            return { success: true, exists: response.data?.exists || false };
        } catch (error) {
            console.error('Error checking organization code:', error);
            return { success: false, error: error.message };
        }
    }

    async createOrganization(organizationData) {
        console.log('Creating organization:', organizationData);

        try {
            const response = await this.post('/organization', organizationData);
            console.log('Organization created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating organization:', error);
            return { success: false, error: error.message };
        }
    }

    async updateOrganization(id, organizationData) {
        console.log('Updating organization ID:', id, 'with data:', organizationData);

        try {
            const response = await this.put(`/organization/${id}`, organizationData);
            console.log('Organization updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating organization:', error);
            return { success: false, error: error.message };
        }
    }

    async deleteOrganization(id) {
        console.log('Deleting organization with ID:', id);

        try {
            const response = await this.delete(`/organization/${id}`);
            console.log('Organization deleted successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error deleting organization:', error);
            return { success: false, error: error.message };
        }
    }

    async generateAccessAndInvoice(subscription_id) {
        console.log('Generating invoice for subscription id:', subscription_id);

        try {
            const response = await this.post(`/organization_generate_access_and_invoice/${subscription_id}`);
            console.log('Invoice generated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error generating invoice:', error);
            return { success: false, error: error.message };
        }
    }

    // Subscription APIs
    async getSubscriptions(page = 1, limit = 10, organizationId = null) {
        console.log('Fetching subscriptions:', { page, limit, organizationId });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(organizationId && { organizationId })
            });

            const response = await this.get(`/subscription?${queryParams}`);
            // console.log('Subscriptions fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            return { success: false, error: error.message };
        }
    }

    async getSubscription(id) {
        console.log('Fetching subscription with ID:', id);

        try {
            const response = await this.get(`/subscription/${id}`);
            console.log('Subscription fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching subscription:', error);
            return { success: false, error: error.message };
        }
    }

    async createSubscription(subscriptionData) {
        console.log('Creating subscription:', subscriptionData);

        try {
            const response = await this.post('/subscription', subscriptionData);
            console.log('Subscription created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating subscription:', error);
            return { success: false, error: error.message };
        }
    }

    async updateSubscription(id, subscriptionData) {
        console.log('Updating subscription ID:', id, 'with data:', subscriptionData);

        try {
            const response = await this.put(`/subscription/${id}`, subscriptionData);
            console.log('Subscription updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating subscription:', error);
            return { success: false, error: error.message };
        }
    }

    // Billing APIs
    async getInvoices(page = 1, limit = 10, organizationId = null) {
        console.log('Fetching invoices:', { page, limit, organizationId });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(organizationId && { organizationId })
            });

            const response = await this.get(`/invoice?${queryParams}`);
            // console.log('Invoices fetched successfully, count:', response.data?.invoices?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching invoices:', error);
            return { success: false, error: error.message };
        }
    }

    async getPayments(page = 1, limit = 10, organizationId = null) {
        console.log('Fetching payments:', { page, limit, organizationId });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(organizationId && { organizationId })
            });

            const response = await this.get(`/payment?${queryParams}`);
            // console.log('Payments fetched successfully, count:', response.data?.payments?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching payments:', error);
            return { success: false, error: error.message };
        }
    }

    async getErpInvoices() {
        console.log('Fetching ERP invoices');

        try {
            const response = await this.get('/erp_invoice');
            // console.log('ERP invoices fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching ERP invoices:', error);
            return { success: false, error: error.message };
        }
    }

    async getErpCustomers() {
        console.log('Fetching ERP customers');

        try {
            const response = await this.get('/organization/erp_customers');
            console.log('ERP customers fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching ERP customers:', error);
            return { success: false, error: error.message };
        }
    }

    async createInvoice(invoiceData) {
        console.log('Creating invoice:', invoiceData);

        try {
            const response = await this.post('/invoice', invoiceData);
            console.log('Invoice created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating invoice:', error);
            return { success: false, error: error.message };
        }
    }

    async recordPayment(paymentData) {
        console.log('Recording payment:', paymentData);

        try {
            const response = await this.post('/payment', paymentData);
            console.log('Payment recorded successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error recording payment:', error);
            return { success: false, error: error.message };
        }
    }

    async updateBillingDataFromErp() {
        console.log('Updating billing data from ERP');

        try {
            const response = await this.post('/update_billing_data_from_erp', {});
            console.log('Billing data updated from ERP successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating billing data from ERP:', error);
            return { success: false, error: error.message };
        }
    }

    // User APIs
    async getPlatformUsers(category = null, page = 1, limit = 10, search = '') {
        console.log('Fetching users:', { category, page, limit, search });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search })
            });

            const response = await this.get(`/platform_user_profile?${queryParams}`);
            // console.log('Users fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching users:', error);
            return { success: false, error: error.message };
        }
    }

    async getPlatformUser(id) {
        console.log('Fetching user with ID:', id);

        try {
            const response = await this.get(`/platform_user_profile/${id}`);
            console.log('User fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching user:', error);
            return { success: false, error: error.message };
        }
    }

    async createPlatformUser(userData) {
        console.log('Creating user:', userData);

        try {
            const response = await this.post('/platform_user_profile', userData);
            console.log('User created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating user:', error);
            return { success: false, error: error.message };
        }
    }

    async updatePlatformUser(id, userData) {
        console.log('Updating user ID:', id, 'with data:', userData);

        try {
            const response = await this.put(`/platform_user_profile_basic/${id}`, userData);
            console.log('User updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating user:', error);
            return { success: false, error: error.message };
        }
    }

    async togglePlatformUserStatus(id, isActive) {
        console.log('Toggling user status for ID:', id, 'to active:', isActive);

        try {
            const response = await this.put(`/platform_user_profile/${id}/status`, { isActive });
            console.log('User status toggled successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error toggling user status:', error);
            return { success: false, error: error.message };
        }
    }

    // User APIs
    async getEnterpriseUsers(category = 'customer', page = 1, limit = 10, search = '') {
        console.log('Fetching users:', { category, page, limit, search });

        try {
            const queryParams = new URLSearchParams({
                category,
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search })
            });

            const response = await this.get(`/enterprise_user_profile?${queryParams}`);
            // console.log('Enterprise users fetched successfully:', response);
            // console.log('Users fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching users:', error);
            return { success: false, error: error.message };
        }
    }

    async getEnterpriseUser(id) {
        console.log('Fetching user with ID:', id);

        try {
            const response = await this.get(`/enterprise_user_profile/${2}`);
            console.log('User fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching user:', error);
            return { success: false, error: error.message };
        }
    }

    async createEnterpriseUser(userData) {
        console.log('Creating user:', userData);

        try {
            const response = await this.post('/enterprise_user_profile', userData);
            console.log('User created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating user:', error);
            return { success: false, error: error.message };
        }
    }

    async updateEnterpriseUser(id, userData) {
        console.log('Updating user ID:', id, 'with data:', userData);

        try {
            const response = await this.put(`/enterprise_user_profile/${id}`, userData);
            console.log('User updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating user:', error);
            return { success: false, error: error.message };
        }
    }

    async toggleEnterpriseUserStatus(id, isActive) {
        console.log('Toggling user status for ID:', id, 'to active:', isActive);

        try {
            const response = await this.put(`/enterprise_user_profile/${id}/status`, { isActive });
            console.log('User status toggled successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error toggling user status:', error);
            return { success: false, error: error.message };
        }
    }


    // Offerings APIs
    async getOfferings(page = 1, limit = 10, filters = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            if (filters.status === 'active') queryParams.set('is_active', 'true');
            else if (filters.status === 'inactive') queryParams.set('is_active', 'false');
            if (filters.billing_cycle_id) queryParams.set('billing_cycle_id', filters.billing_cycle_id.toString());

            const response = await this.get(`/offering?${queryParams}`);
            //console.log('Offerings fetched successfully, count:', response.data);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching offerings:', error);
            return { success: false, error: error.message };
        }
    }

    async getOffering(id) {
        console.log('Fetching offering with ID:', id);

        try {
            const response = await this.get(`/offering/${id}`);
            console.log('Offering fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching offering:', error);
            return { success: false, error: error.message };
        }
    }

    async createOffering(offeringData) {
        console.log('Creating offering:', offeringData);

        try {
            const response = await this.post('/offering', offeringData);
            console.log('Offering created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating offering:', error);
            return { success: false, error: error.message };
        }
    }

    async updateOffering(id, offeringData) {
        console.log('Updating offering ID:', id, 'with data:', offeringData);

        try {
            const response = await this.put(`/offering/${id}`, offeringData);
            console.log('Offering updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating offering:', error);
            return { success: false, error: error.message };
        }
    }

    // Billing Cycles APIs
    async getBillingCycles() {
        console.log('Fetching billing cycles');

        try {
            const response = await this.get('/billing_cycle');
            // console.log('Billing cycles fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching billing cycles:', error);
            return { success: false, error: error.message };
        }
    }

    async getBillingCycle(id) {
        console.log('Fetching billing cycle with ID:', id);

        try {
            const response = await this.get(`/billing_cycle/${id}`);
            console.log('Billing cycle fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching billing cycle:', error);
            return { success: false, error: error.message };
        }
    }

    async createBillingCycle(billingCycleData) {
        console.log('Creating billing cycle:', billingCycleData);

        try {
            const response = await this.post('/billing_cycle', billingCycleData);
            console.log('Billing cycle created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating billing cycle:', error);
            return { success: false, error: error.message };
        }
    }

    async updateBillingCycle(id, billingCycleData) {
        console.log('Updating billing cycle ID:', id, 'with data:', billingCycleData);

        try {
            const response = await this.put(`/billing_cycle/${id}`, billingCycleData);
            console.log('Billing cycle updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating billing cycle:', error);
            return { success: false, error: error.message };
        }
    }

    // Currency APIs
    async getCurrencies() {
        console.log('Fetching currencies');

        try {
            const response = await this.get('/currency');
            // console.log('Currencies fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching currencies:', error);
            return { success: false, error: error.message };
        }
    }

    async getCurrency(id) {
        console.log('Fetching currency with ID:', id);

        try {
            const response = await this.get(`/currency/${id}`);
            console.log('Currency fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching currency:', error);
            return { success: false, error: error.message };
        }
    }

    async createCurrency(currencyData) {
        console.log('Creating currency:', currencyData);

        try {
            const response = await this.post('/currency', currencyData);
            console.log('Currency created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating currency:', error);
            return { success: false, error: error.message };
        }
    }

    async updateCurrency(id, currencyData) {
        console.log('Updating currency ID:', id, 'with data:', currencyData);

        try {
            const response = await this.put(`/currency/${id}`, currencyData);
            console.log('Currency updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating currency:', error);
            return { success: false, error: error.message };
        }
    }

    // Country APIs
    async getCountries() {
        console.log('Fetching countries');

        try {
            const response = await this.get('/country');
            // console.log('Countries fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching countries:', error);
            return { success: false, error: error.message };
        }
    }

    async getCountry(id) {
        console.log('Fetching country with ID:', id);

        try {
            const response = await this.get(`/country/${id}`);
            console.log('Country fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching country:', error);
            return { success: false, error: error.message };
        }
    }

    async createCountry(countryData) {
        console.log('Creating country:', countryData);

        try {
            const response = await this.post('/country', countryData);
            console.log('Country created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating country:', error);
            return { success: false, error: error.message };
        }
    }

    async updateCountry(id, countryData) {
        console.log('Updating country ID:', id, 'with data:', countryData);

        try {
            const response = await this.put(`/country/${id}`, countryData);
            console.log('Country updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating country:', error);
            return { success: false, error: error.message };
        }
    }

    // Version checking API
    async checkAppVersion() {
        console.log('Checking app version against server');

        try {
            const response = await this.get('/version/webapp/enterprise', { useTimeout: true });
            console.log('Version check completed successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error checking app version:', error);
            return { success: false, error: error.message };
        }
    }

    // Audit APIs
    async getAuditLogs(page = 1, limit = 10, search = '') {
        console.log('Fetching audit logs', { page, limit, search });

        try {
            const params = new URLSearchParams({ page, limit, search });
            const response = await this.get(`/audit?${params}`);
            console.log('Audit logs fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            return { success: false, error: error.message };
        }
    }

    // Organization Access Rights APIs
    async getOrganizationAccessRights(organizationId, page = 1, limit = 10, search = '') {
        console.log('Fetching organization access rights:', { organizationId, page, limit, search });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search })
            });

            const response = await this.get(`/platform_access_right/by_organization_id/${organizationId}?${queryParams}`);
            console.log('Organization access rights fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching organization access rights:', error);
            return { success: false, error: error.message };
        }
    }

    async getOrganizationAccessRight(id) {
        console.log('Fetching organization access right with ID:', id);

        try {
            const response = await this.get(`/organization/access-rights/${id}`);
            console.log('Organization access right fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching organization access right:', error);
            return { success: false, error: error.message };
        }
    }

    async createOrganizationAccessRight(accessRightData) {
        console.log('Creating organization access right:', accessRightData);

        try {
            const response = await this.post('/organization/access-rights', accessRightData);
            console.log('Organization access right created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating organization access right:', error);
            return { success: false, error: error.message };
        }
    }

    async updateOrganizationAccessRight(id, accessRightData) {
        console.log('Updating organization access right ID:', id, 'with data:', accessRightData);

        try {
            const response = await this.put(`/organization/access-rights/${id}`, accessRightData);
            console.log('Organization access right updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating organization access right:', error);
            return { success: false, error: error.message };
        }
    }

    async deleteOrganizationAccessRight(id) {
        console.log('Deleting organization access right with ID:', id);

        try {
            const response = await this.delete(`/organization/access-rights/${id}`);
            console.log('Organization access right deleted successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error deleting organization access right:', error);
            return { success: false, error: error.message };
        }
    }

    // Platform Roles APIs
    async getPlatformRoles(page = 1, limit = 10, search = '') {
        console.log('Fetching platform roles:', { page, limit, search });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search })
            });

            const response = await this.get(`/platform_role?${queryParams}`);
            // console.log('Platform roles fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching platform roles:', error);
            return { success: false, error: error.message };
        }
    }

    async getPlatformRolesByOrganization(organizationId, page = 1, limit = 10, search = '') {
        console.log('Fetching platform roles for organization:', { organizationId, page, limit, search });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search })
            });

            const response = await this.get(`/platform_role/by_organization/${organizationId}?${queryParams}`);
            //console.log('Platform roles fetched successfully for organization, count:', response.data?.length);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching platform roles for organization:', error);
            return { success: false, error: error.message };
        }
    }

    async getPlatformRole(id) {
        console.log('Fetching platform role with ID:', id);

        try {
            const response = await this.get(`/platform_role/${id}`);
            console.log('Platform role fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching platform role:', error);
            return { success: false, error: error.message };
        }
    }

    async createPlatformRole(roleData) {
        console.log('Creating platform role:', roleData);

        try {
            const response = await this.post('/platform_role', roleData);
            console.log('Platform role created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating platform role:', error);
            return { success: false, error: error.message };
        }
    }

    async updatePlatformRole(id, roleData) {
        console.log('Updating platform role ID:', id, 'with data:', roleData);

        try {
            const response = await this.put(`/platform_role/${id}`, roleData);
            console.log('Platform role updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating platform role:', error);
            return { success: false, error: error.message };
        }
    }

    async deletePlatformRole(id) {
        console.log('Deleting platform role with ID:', id);

        try {
            const response = await this.delete(`/platform_role/${id}`);
            console.log('Platform role deleted successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error deleting platform role:', error);
            return { success: false, error: error.message };
        }
    }

    // Permissions APIs for Platform Roles
    async getAllPermissionsByOrganization(organization_id) {
        console.log('Fetching list of all permissions for organization ID:', organization_id);
        try {
            const response = await this.get(`/platform_access_right/by_organization_id/${organization_id}`);
            console.log('Permissions fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching permissions:', error);
            return { success: false, error: error.message };
        }
    }

    async getPlatformRolePermissions(roleId) {
        console.log('Fetching permissions for platform role ID:', roleId);
        try {
            // Use legacy/explicit endpoint for role permissions
            const response = await this.get(`/platform_permission_by_platform_role/${roleId}`);
            console.log('Role permissions fetched');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching role permissions:', error);
            return { success: false, error: error.message };
        }
    }

    async getRolesByPlatformAccessRight(accessRightId) {
        console.log('Fetching roles connected to platform access right ID:', accessRightId);
        try {
            const response = await this.get(`/platform_role/by_platform_access_right/${accessRightId}`);
            console.log('Connected roles fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching roles by platform access right:', error);
            return { success: false, error: error.message };
        }
    }

    async getPlatformAccessRightsByOrganization(organizationId) {
        console.log('Fetching platform access rights for organization ID:', organizationId);
        try {
            const response = await this.get(`/platform_access_right/by_organization_id/${organizationId}`);
            console.log('Platform access rights fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching platform permissions by organization:', error);
            return { success: false, error: error.message };
        }
    }

    async createPlatformAccessRight(accessRightData) {
        console.log('Creating platform access right:', accessRightData);
        try {
            const response = await this.post('/platform_access_right', accessRightData);
            console.log('Platform access right created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating platform access right:', error);
            return { success: false, error: error.message };
        }
    }

    async updatePlatformAccessRight(id, accessRightData) {
        console.log('Updating platform access right ID:', id, 'data:', accessRightData);
        try {
            const response = await this.put(`/platform_access_right/${id}`, accessRightData);
            console.log('Platform access right updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating platform access right:', error);
            return { success: false, error: error.message };
        }
    }

    // Fiscal Entity APIs
    async getFiscalEntities() {
        console.log('Fetching list of all fiscal entities');
        try {
            const response = await this.get('/fiscal_entity');
            console.log('Fiscal entities fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching fiscal entities:', error);
            return { success: false, error: error.message };
        }
    }

    async getFiscalEntityById(id) {
        console.log('Fetching fiscal entity by ID:', id);
        try {
            const response = await this.get(`/fiscal_entity/${id}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching fiscal entity:', error);
            return { success: false, error: error.message };
        }
    }

    async getFiscalEntitiesByOrganization(organizationId) {
        console.log('Fetching fiscal entities for organization ID:', organizationId);
        try {
            const response = await this.get(`/fiscal_entity/by_organization/${organizationId}`);
            console.log('Fiscal entities by organization fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching fiscal entities by organization:', error);
            return { success: false, error: error.message };
        }
    }

    async createFiscalEntity(fiscalEntityData) {
        console.log('Creating fiscal entity:', fiscalEntityData);
        try {
            const response = await this.post('/fiscal_entity', fiscalEntityData);
            console.log('Fiscal entity created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating fiscal entity:', error);
            return { success: false, error: error.message };
        }
    }

    async updateFiscalEntity(id, fiscalEntityData) {
        console.log('Updating fiscal entity ID:', id, 'data:', fiscalEntityData);
        try {
            const response = await this.put(`/fiscal_entity/${id}`, fiscalEntityData);
            console.log('Fiscal entity updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating fiscal entity:', error);
            return { success: false, error: error.message };
        }
    }

    async getFiscalEntityAvailableErpList() {
        console.log('Fetching fiscal entity available ERP list');
        try {
            const response = await this.get('/fiscal_entity_available_erp_list');
            console.log('Fiscal entity available ERP list fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching fiscal entity available ERP list:', error);
            return { success: false, error: error.message };
        }
    }

    async updatePlatformRolePermissions(roleId, permissions = []) {
        console.log('Updating permissions for platform role ID:', roleId, 'permissions:', permissions);
        try {
            const response = await this.put(`/platform_role/${roleId}/permissions`, { permissions });
            console.log('Role permissions updated');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating role permissions:', error);
            return { success: false, error: error.message };
        }
    }

    // Enterprise Permission APIs
    async getAllEnterpriseAccessRights() {
        console.log('Fetching list of all enterprise access rights');
        try {
            const response = await this.get('/enterprise_access_right');
            console.log('Enterprise access rights fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching enterprise access rights:', error);
            return { success: false, error: error.message };
        }
    }

    async createEnterpriseAccessRight(accessRightData) {
        console.log('Creating enterprise access right:', accessRightData);
        try {
            const response = await this.post('/enterprise_access_right', accessRightData);
            console.log('Enterprise access right created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating enterprise access right:', error);
            return { success: false, error: error.message };
        }
    }

    async updateEnterpriseAccessRight(id, accessRightData) {
        console.log('Updating enterprise access right ID:', id, 'data:', accessRightData);
        try {
            const response = await this.put(`/enterprise_access_right/${id}`, accessRightData);
            console.log('Enterprise access right updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating enterprise access right:', error);
            return { success: false, error: error.message };
        }
    }

    async getEnterpriseRolePermissions(roleId) {
        console.log('Fetching permissions for enterprise role ID:', roleId);
        try {
            const response = await this.get(`/enterprise_permission_by_enterprise_role/${roleId}`);
            console.log('Enterprise role permissions fetched');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching enterprise role permissions:', error);
            return { success: false, error: error.message };
        }
    }

    async getRolesByEnterpriseAccessRight(accessRightId) {
        console.log('Fetching roles connected to enterprise access right ID:', accessRightId);
        try {
            const response = await this.get(`/enterprise_role/by_enterprise_access_right/${accessRightId}`);
            console.log('Connected enterprise roles fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching roles by enterprise access right:', error);
            return { success: false, error: error.message };
        }
    }

    async updateEnterpriseRolePermissions(roleId, permissions = []) {
        console.log('Updating permissions for enterprise role ID:', roleId, 'permissions:', permissions);
        try {
            const response = await this.put(`/enterprise_role/${roleId}/permissions`, { permissions });
            console.log('Enterprise role permissions updated');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating enterprise role permissions:', error);
            return { success: false, error: error.message };
        }
    }

    // Enterprise Roles APIs
    async getEnterpriseRoles(page = 1, limit = 10, search = '') {
        console.log('Fetching enterprise roles:', { page, limit, search });

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search })
            });

            const response = await this.get(`/enterprise_role?${queryParams}`);
            // console.log('Enterprise roles fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching enterprise roles:', error);
            return { success: false, error: error.message };
        }
    }

    async getEnterpriseRole(id) {
        console.log('Fetching enterprise role with ID:', id);

        try {
            const response = await this.get(`/enterprise_role/${id}`);
            console.log('Enterprise role fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching enterprise role:', error);
            return { success: false, error: error.message };
        }
    }

    async createEnterpriseRole(roleData) {
        console.log('Creating enterprise role:', roleData);

        try {
            const response = await this.post('/enterprise_role', roleData);
            console.log('Enterprise role created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating enterprise role:', error);
            return { success: false, error: error.message };
        }
    }

    async updateEnterpriseRole(id, roleData) {
        console.log('Updating enterprise role ID:', id, 'with data:', roleData);

        try {
            const response = await this.put(`/enterprise_role/${id}`, roleData);
            console.log('Enterprise role updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating enterprise role:', error);
            return { success: false, error: error.message };
        }
    }

    async deleteEnterpriseRole(id) {
        console.log('Deleting enterprise role with ID:', id);

        try {
            const response = await this.delete(`/enterprise_role/${id}`);
            console.log('Enterprise role deleted successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error deleting enterprise role:', error);
            return { success: false, error: error.message };
        }
    }

    // Enterprise Teams APIs
    async getEnterpriseTeams(search = '', isActive = undefined) {
        console.log('Fetching enterprise teams:', { search, isActive });

        try {
            const queryParams = new URLSearchParams({
                ...(search && { name: search }),
                ...(isActive !== undefined && isActive !== '' && { is_active: String(isActive) })
            });

            const queryString = queryParams.toString();
            const response = await this.get(`/enterprise_team${queryString ? `?${queryString}` : ''}`);
            // console.log('Enterprise teams fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            console.error('Error fetching enterprise teams:', error);
            return { success: false, error: error.message };
        }
    }

    async getActiveEnterpriseTeams() {
        console.log('Fetching active enterprise teams');

        try {
            const response = await this.get('/enterprise_team/active');
            console.log('Active enterprise teams fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching active enterprise teams:', error);
            return { success: false, error: error.message };
        }
    }

    async getEnterpriseTeam(id) {
        console.log('Fetching enterprise team with ID:', id);

        try {
            const response = await this.get(`/enterprise_team/${id}`);
            console.log('Enterprise team fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching enterprise team:', error);
            return { success: false, error: error.message };
        }
    }

    async createEnterpriseTeam(teamData) {
        console.log('Creating enterprise team:', teamData);

        try {
            const response = await this.post('/enterprise_team', teamData);
            console.log('Enterprise team created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating enterprise team:', error);
            return { success: false, error: error.message };
        }
    }

    async updateEnterpriseTeam(id, teamData) {
        console.log('Updating enterprise team ID:', id, 'with data:', teamData);

        try {
            const response = await this.put(`/enterprise_team/${id}`, teamData);
            console.log('Enterprise team updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating enterprise team:', error);
            return { success: false, error: error.message };
        }
    }

    async archiveEnterpriseTeam(id) {
        console.log('Archiving enterprise team with ID:', id);

        try {
            const response = await this.put(`/enterprise_team/${id}/archive`, {});
            console.log('Enterprise team archived successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error archiving enterprise team:', error);
            return { success: false, error: error.message };
        }
    }

    async activateEnterpriseTeam(id) {
        console.log('Activating enterprise team with ID:', id);

        try {
            const response = await this.put(`/enterprise_team/${id}/activate`, {});
            console.log('Enterprise team activated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error activating enterprise team:', error);
            return { success: false, error: error.message };
        }
    }

    async deleteEnterpriseTeam(id) {
        console.log('Deleting enterprise team with ID:', id);

        try {
            const response = await this.delete(`/enterprise_team/${id}`);
            console.log('Enterprise team deleted successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error deleting enterprise team:', error);
            return { success: false, error: error.message };
        }
    }

    // Enterprise Team Membership APIs
    async getEnterpriseTeamUsers() {
        console.log('Fetching all enterprise user team assignments');

        try {
            const response = await this.get('/enterprise_team_user');
            // console.log('Enterprise user team assignments fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching enterprise user team assignments:', error);
            return { success: false, error: error.message };
        }
    }

    async getEnterpriseTeamMembers(teamId) {
        console.log('Fetching members for enterprise team ID:', teamId);

        try {
            const response = await this.get(`/enterprise_team_user/team/${teamId}/users`);
            console.log('Enterprise team members fetched successfully, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching enterprise team members:', error);
            return { success: false, error: error.message };
        }
    }

    async getEnterpriseTeamsForUser(userAccountId) {
        console.log('Fetching enterprise teams for user account ID:', userAccountId);

        try {
            const response = await this.get(`/enterprise_team_user/user/${userAccountId}`);
            console.log('User enterprise teams fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching enterprise teams for user:', error);
            return { success: false, error: error.message };
        }
    }

    async assignUsersToEnterpriseTeam(teamId, userAccountIds = []) {
        console.log('Assigning users to enterprise team:', { teamId, userAccountIds });

        try {
            const response = await this.post('/enterprise_team_user/bulk', {
                enterprise_team_id: teamId,
                user_account_ids: userAccountIds
            });
            console.log('Users assigned to enterprise team successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error assigning users to enterprise team:', error);
            return { success: false, error: error.message };
        }
    }

    async removeUserFromEnterpriseTeam(teamId, userAccountId) {
        console.log('Removing user from enterprise team:', { teamId, userAccountId });

        try {
            const response = await this.delete(`/enterprise_team_user/user/${userAccountId}/team/${teamId}`);
            console.log('User removed from enterprise team successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error removing user from enterprise team:', error);
            return { success: false, error: error.message };
        }
    }

    // Role Assignment APIs
    async assignRoleToUser(userId, roleId) {
        console.log('Assigning role to user:', { userId, roleId });

        try {
            const response = await this.post('/enterprise_user_role', {
                user_account_id: userId,
                enterprise_role_id: roleId
            });
            console.log('Role assigned successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error assigning role:', error);
            return { success: false, error: error.message };
        }
    }

    async getUserRoles(userId) {
        console.log('Fetching user roles for user ID:', userId);

        try {
            const response = await this.get(`/enterprise_user_role/by_user/${userId}`);
            console.log('User roles fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching user roles:', error);
            return { success: false, error: error.message };
        }
    }

    async removeRoleFromUser(userId, roleId) {
        console.log('Removing role from user:', { userId, roleId });

        try {
            const response = await this.delete(`/enterprise_user_role_remove/${userId}/role/${roleId}`);
            console.log('Role removed successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error removing role:', error);
            return { success: false, error: error.message };
        }
    }

    // Organization Default Settings APIs
    async getOrganizationDefaultSettingByCode(code) {
        console.log('Fetching organization default setting with code:', code);

        try {
            const response = await this.get(`/organization_default_setting/by_code/${code}`);
            console.log('Organization default setting fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching organization default setting:', error);
            return { success: false, error: error.message };
        }
    }

    async createOrganizationDefaultSetting(settingData) {
        console.log('Creating organization default setting:', settingData);

        try {
            const response = await this.post('/organization_default_setting', settingData);
            console.log('Organization default setting created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating organization default setting:', error);
            return { success: false, error: error.message };
        }
    }

    async updateOrganizationDefaultSetting(id, settingData) {
        console.log('Updating organization default setting ID:', id, 'with data:', settingData);

        try {
            const response = await this.put(`/organization_default_setting/${id}`, settingData);
            console.log('Organization default setting updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating organization default setting:', error);
            return { success: false, error: error.message };
        }
    }

    // Token utility methods
    decodeToken(token) {
        try {
            if (!token) return null;

            // JWT tokens have 3 parts separated by dots
            const parts = token.split('.');
            if (parts.length !== 3) return null;

            // Decode the payload (second part)
            const payload = JSON.parse(atob(parts[1]));
            return payload;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    }

    getTokenExpiry(token = null) {
        try {
            const tokenToCheck = token || this.access_token;
            if (!tokenToCheck) return null;

            const payload = this.decodeToken(tokenToCheck);
            if (!payload || !payload.exp) return null;

            // JWT exp is in seconds, convert to milliseconds
            return payload.exp * 1000;
        } catch (error) {
            console.error('Error getting token expiry:', error);
            return null;
        }
    }

    isTokenExpired(token = null) {
        const expiryTime = this.getTokenExpiry(token);
        if (!expiryTime) return true; // No expiry time means expired

        return Date.now() >= expiryTime;
    }

    getTimeUntilExpiry(token = null) {
        const expiryTime = this.getTokenExpiry(token);
        if (!expiryTime) return 0;

        const timeLeft = expiryTime - Date.now();
        return timeLeft > 0 ? timeLeft : 0;
    }

    // Token refresh method (implement based on your API)
    async refreshToken() {
        try {
            console.log('Attempting to refresh token...');

            // If your API supports token refresh, implement it here
            // For now, we'll check the current token and return appropriate response

            if (!this.access_token) {
                throw new Error('No token available to refresh');
            }

            if (this.isTokenExpired()) {
                throw new Error('Token has already expired');
            }

            // Example refresh implementation (adjust based on your API)
            // const response = await this.post('/auth/refresh', { token: this.access_token });
            // 
            // if (response.data.access_token) {
            //     this.access_token = response.data.access_token;
            //     localStorage.setItem(this.access_tokenKey, this.access_token);
            //     return { success: true, token: response.data.access_token };
            // }

            // For now, return success if token is still valid
            const timeLeft = this.getTimeUntilExpiry();
            if (timeLeft > 0) {
                console.log('Token is still valid, no refresh needed');
                return { success: true, token: this.access_token, timeLeft };
            }

            throw new Error('Token refresh not implemented');

        } catch (error) {
            console.error('Error refreshing token:', error);
            return { success: false, error: error.message };
        }
    }

    // Utility methods
    formatDate(dateString) {
        // Handle Unix timestamps (in seconds) by converting to milliseconds
        const timestamp = typeof dateString === 'number' ? dateString * 1000 : dateString;
        const formatted = new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
        return formatted;
    }

    formatDateTime(dateString) {
        // Handle Unix timestamps (in seconds) by converting to milliseconds
        const timestamp = typeof dateString === 'number' ? dateString * 1000 : dateString;
        const formatted = new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        //console.log('Formatted datetime result:', formatted);
        return formatted;
    }

    formatCurrency(amount) {
        //console.log('Formatting currency:', amount);
        const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
        //console.log('Formatted currency result:', formatted);
        return formatted;
    }

    async getHourlySalesVolume(organizationId) {
        console.log('Fetching hourly sales volume for organization:', organizationId);

        try {
            const response = await this.get(`/enterprise/utilization/hourly_sales_volume/${organizationId}`);
            console.log('Hourly sales volume fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching hourly sales volume:', error);
            return { success: false, error: error.message };
        }
    }

    async getHourlySuccessfulLogins(organizationId) {
        // console.log('Fetching hourly successful logins for organization:', organizationId);

        try {
            const response = await this.get(`/platform_login_attempt/hourly_successful/${organizationId}`);

            // console.log('Hourly successful logins fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching hourly successful logins:', error);
            return { success: false, error: error.message };
        }
    }
    async getDailySalesVolume(organizationId) {
        console.log('Fetching daily sales volume for organization:', organizationId);

        try {
            const response = await this.get(`/enterprise/utilization/daily_sales_volume/${organizationId}`);
            console.log('Daily sales volume fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching daily sales volume:', error);
            return { success: false, error: error.message };
        }
    }

    async getDailySuccessfulLogins(organizationId) {
        console.log('Fetching daily successful logins for organization:', organizationId);

        try {
            const response = await this.get(`/enterprise/utilization/daily_successful_logins/${organizationId}`);
            console.log('Daily successful logins fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching daily successful logins:', error);
            return { success: false, error: error.message };
        }
    }

    async getDailyStockTransferOrders(organizationId) {
        console.log('Fetching daily stock transfer orders for organization:', organizationId);

        try {
            const response = await this.get(`/enterprise/utilization/daily_successful_stock_transfer_orders/${organizationId}`);
            console.log('Daily stock transfer orders fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching daily stock transfer orders:', error);
            return { success: false, error: error.message };
        }
    }

    async getUtilizationMetrics(organizationId) {
        console.log('Fetching utilization metrics for organization:', organizationId);

        try {
            const response = await this.get(`/enterprise/utilization/metrics/${organizationId}`);
            console.log('Utilization metrics fetched successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching utilization metrics:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Brand APIs ====================
    async getBrandsByOrganization(organizationId) {
        console.log('Fetching brands for organization ID:', organizationId);
        try {
            const response = await this.get(`/brand/by_organization_id/${organizationId}`);
            console.log('Brands fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching brands:', error);
            return { success: false, error: error.message };
        }
    }

    async createBrand(data) {
        console.log('Creating brand:', data);
        try {
            const response = await this.post('/brand', data);
            console.log('Brand created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating brand:', error);
            return { success: false, error: error.message };
        }
    }

    async updateBrand(id, data) {
        console.log('Updating brand ID:', id, 'data:', data);
        try {
            const response = await this.put(`/brand/${id}`, data);
            console.log('Brand updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating brand:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Reconciliation Report Type APIs ====================
    async getReconciliationReportTypesByOrganization(organizationId) {
        console.log('Fetching reconciliation report types for organization ID:', organizationId);
        try {
            const response = await this.get(`/reconciliation_report_type/by_organization_id/${organizationId}`);
            console.log('Reconciliation report types fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching reconciliation report types:', error);
            return { success: false, error: error.message };
        }
    }

    async createReconciliationReportType(data) {
        console.log('Creating reconciliation report type:', data);
        try {
            const response = await this.post('/reconciliation_report_type', data);
            console.log('Reconciliation report type created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating reconciliation report type:', error);
            return { success: false, error: error.message };
        }
    }

    async updateReconciliationReportType(id, data) {
        console.log('Updating reconciliation report type ID:', id, 'data:', data);
        try {
            const response = await this.put(`/reconciliation_report_type/${id}`, data);
            console.log('Reconciliation report type updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating reconciliation report type:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Product APIs ====================
    async getProductsByOrganization(organizationId) {
        console.log('Fetching products for organization ID:', organizationId);
        try {
            const response = await this.get(`/product/by_organization_id/${organizationId}`);
            console.log('Products fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching products:', error);
            return { success: false, error: error.message };
        }
    }

    async createProduct(data) {
        console.log('Creating product:', data);
        try {
            const response = await this.post('/product', data);
            console.log('Product created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating product:', error);
            return { success: false, error: error.message };
        }
    }

    async updateProduct(id, data) {
        console.log('Updating product ID:', id, 'data:', data);
        try {
            const response = await this.put(`/product/${id}`, data);
            console.log('Product updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating product:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Unit of Measure APIs ====================
    async getUnitsOfMeasureByOrganization(organizationId) {
        console.log('Fetching units of measure for organization ID:', organizationId);
        try {
            const response = await this.get(`/unit_of_measure/by_organization_id/${organizationId}`);
            console.log('Units of measure fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching units of measure:', error);
            return { success: false, error: error.message };
        }
    }

    async createUnitOfMeasure(data) {
        console.log('Creating unit of measure:', data);
        try {
            const response = await this.post('/unit_of_measure', data);
            console.log('Unit of measure created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating unit of measure:', error);
            return { success: false, error: error.message };
        }
    }

    async updateUnitOfMeasure(id, data) {
        console.log('Updating unit of measure ID:', id, 'data:', data);
        try {
            const response = await this.put(`/unit_of_measure/${id}`, data);
            console.log('Unit of measure updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating unit of measure:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Storage Location Type APIs ====================
    async getStorageLocationTypesByOrganization(organizationId) {
        console.log('Fetching storage location types for organization ID:', organizationId);
        try {
            const response = await this.get(`/storage_location_type/by_organization_id/${organizationId}`);
            console.log('Storage location types fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching storage location types:', error);
            return { success: false, error: error.message };
        }
    }

    async createStorageLocationType(data) {
        console.log('Creating storage location type:', data);
        try {
            const response = await this.post('/storage_location_type', data);
            console.log('Storage location type created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating storage location type:', error);
            return { success: false, error: error.message };
        }
    }

    async updateStorageLocationType(id, data) {
        console.log('Updating storage location type ID:', id, 'data:', data);
        try {
            const response = await this.put(`/storage_location_type/${id}`, data);
            console.log('Storage location type updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating storage location type:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Sale Tag Value APIs ====================
    async getSaleTagValuesByOrganization(organizationId) {
        console.log('Fetching sale tag values for organization ID:', organizationId);
        try {
            const response = await this.get(`/sale_tag_value/by_organization_id/${organizationId}`);
            console.log('Sale tag values fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching sale tag values:', error);
            return { success: false, error: error.message };
        }
    }

    async createSaleTagValue(data) {
        console.log('Creating sale tag value:', data);
        try {
            const response = await this.post('/sale_tag_value', data);
            console.log('Sale tag value created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating sale tag value:', error);
            return { success: false, error: error.message };
        }
    }

    async updateSaleTagValue(id, data) {
        console.log('Updating sale tag value ID:', id, 'data:', data);
        try {
            const response = await this.put(`/sale_tag_value/${id}`, data);
            console.log('Sale tag value updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating sale tag value:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Sale Deletion Reason APIs ====================
    async getSaleDeletionReasonsByOrganization(organizationId) {
        console.log('Fetching sale deletion reasons for organization ID:', organizationId);
        try {
            const response = await this.get(`/sale_deletion_reason/by_organization_id/${organizationId}`);
            console.log('Sale deletion reasons fetched, count:', response.data?.length);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching sale deletion reasons:', error);
            return { success: false, error: error.message };
        }
    }

    async createSaleDeletionReason(data) {
        console.log('Creating sale deletion reason:', data);
        try {
            const response = await this.post('/sale_deletion_reason', data);
            console.log('Sale deletion reason created successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error creating sale deletion reason:', error);
            return { success: false, error: error.message };
        }
    }

    async updateSaleDeletionReason(id, data) {
        console.log('Updating sale deletion reason ID:', id, 'data:', data);
        try {
            const response = await this.put(`/sale_deletion_reason/${id}`, data);
            console.log('Sale deletion reason updated successfully');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error updating sale deletion reason:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupUsersWithNoRolesCount(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/users_with_no_roles_count/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching users with no roles count:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupTeamsWithNoMembersCount(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/teams_with_no_members_count/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching teams with no members count:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupSsoWronglyClassifiedStorageLocationCount(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/sso_with_wrongly_classified_storage_location_count/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching SSO wrongly classified storage location count:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupSalesRepWronglyClassifiedStorageLocationCount(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/sales_rep_with_wrongly_classified_storage_location_count/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching sales rep wrongly classified storage location count:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupUsersWithNoRolesList(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/users_with_no_roles_list/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching users with no roles list:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupTeamsWithNoMembersList(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/teams_with_no_members_list/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching teams with no members list:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupSsoWronglyClassifiedStorageLocationList(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/sso_with_wrongly_classified_storage_location_list/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching SSO wrongly classified storage location list:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupSalesRepWronglyClassifiedStorageLocationList(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/sales_rep_with_wrongly_classified_storage_location_list/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching sales rep wrongly classified storage location list:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupUserWithBothSsoAndSalesRepRolesCount(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/user_with_both_sso_and_sales_rep_roles_count/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching users with both SSO and sales rep roles count:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupUserWithBothSsoAndSalesRepRolesList(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/user_with_both_sso_and_sales_rep_roles_list/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching users with both SSO and sales rep roles list:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupFieldUserWithNoTeamCount(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/field_user_with_no_team_count/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching field user with no team count:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupFieldUserWithNoTeamList(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/field_user_with_no_team_list/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching field user with no team list:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupTeamLeaderWithNoTeamAssignedCount(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/team_leader_with_no_team_assigned_count/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching team leader with no team assigned count:', error);
            return { success: false, error: error.message };
        }
    }

    async getSetupTeamLeaderWithNoTeamAssignedList(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/setup/team_leader_with_no_team_assigned_list/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching team leader with no team assigned list:', error);
            return { success: false, error: error.message };
        }
    }

    async getInventoryFrozenSsoStockCount(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/inventory/frozen_sso_stock_count/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching frozen SSO stock count:', error);
            return { success: false, error: error.message };
        }
    }

    async getInventoryFrozenSsoStockList(organizationId) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        try {
            const response = await this.get(`/enterprise/quality/inventory/frozen_sso_stock_list/${orgParam}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching frozen SSO stock list:', error);
            return { success: false, error: error.message };
        }
    }

    async getInventorySoldInStockCount(organizationId, stockType) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        const query = stockType ? `?stock_type=${encodeURIComponent(stockType)}` : '';
        try {
            const response = await this.get(`/enterprise/quality/inventory/sold_in_stock_count/${orgParam}${query}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching sold-in stock count:', error);
            return { success: false, error: error.message };
        }
    }

    async getInventorySoldInStockList(organizationId, stockType) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        const query = stockType ? `?stock_type=${encodeURIComponent(stockType)}` : '';
        try {
            const response = await this.get(`/enterprise/quality/inventory/sold_in_stock_list/${orgParam}${query}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching sold-in stock list:', error);
            return { success: false, error: error.message };
        }
    }

    async getStockMovementMisalignedCount(organizationId, inventoryStatus, orderStatus) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        const query = `?inventory_status=${encodeURIComponent(inventoryStatus)}&order_status=${encodeURIComponent(orderStatus)}`;
        try {
            const response = await this.get(`/enterprise/quality/stock_movement/inventory_movement_with_misaligned_stock_count/${orgParam}${query}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching stock movement misaligned count:', error);
            return { success: false, error: error.message };
        }
    }

    async getStockMovementMisalignedList(organizationId, inventoryStatus, orderStatus) {
        const orgParam = organizationId === 'all' ? 'all' : organizationId;
        const query = `?inventory_status=${encodeURIComponent(inventoryStatus)}&order_status=${encodeURIComponent(orderStatus)}`;
        try {
            const response = await this.get(`/enterprise/quality/stock_movement/inventory_movement_with_misaligned_stock_list/${orgParam}${query}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching stock movement misaligned list:', error);
            return { success: false, error: error.message };
        }
    }

    // Technical / Node Metrics APIs
    async getTechnicalRuntime() {
        try {
            const response = await this.get('/enterprise/technical/api/runtime');
            return { success: true, data: response };
        } catch (error) {
            console.error('Error fetching technical runtime:', error);
            return { success: false, error: error.message };
        }
    }

    async getTechnicalMemory() {
        try {
            const response = await this.get('/enterprise/technical/api/memory');
            return { success: true, data: response };
        } catch (error) {
            console.error('Error fetching technical memory:', error);
            return { success: false, error: error.message };
        }
    }

    async getTechnicalCpu() {
        try {
            const response = await this.get('/enterprise/technical/api/cpu');
            return { success: true, data: response };
        } catch (error) {
            console.error('Error fetching technical CPU:', error);
            return { success: false, error: error.message };
        }
    }

    async getTechnicalOs() {
        try {
            const response = await this.get('/enterprise/technical/api/os');
            return { success: true, data: response };
        } catch (error) {
            console.error('Error fetching technical OS info:', error);
            return { success: false, error: error.message };
        }
    }

    async getTechnicalNetwork() {
        try {
            const response = await this.get('/enterprise/technical/api/network');
            return { success: true, data: response };
        } catch (error) {
            console.error('Error fetching technical network/HTTP metrics:', error);
            return { success: false, error: error.message };
        }
    }

    async getTechnicalHealth() {
        try {
            const response = await this.get('/enterprise/technical/api/health');
            return { success: true, data: response };
        } catch (error) {
            console.error('Error fetching technical health:', error);
            return { success: false, error: error.message };
        }
    }

    async getTechnicalApplication() {
        try {
            const response = await this.get('/enterprise/technical/api/application');
            return { success: true, data: response };
        } catch (error) {
            console.error('Error fetching technical application metrics:', error);
            return { success: false, error: error.message };
        }
    }

    async getDailyPlatformVersionStatistics(organizationId = 'all') {
        try {
            const response = await this.get(`/enterprise/technical/daily_successful_logins_platform_version/${organizationId}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching daily platform version statistics:', error);
            return { success: false, error: error.message };
        }
    }

    async getDailyEnterpriseVersionStatistics() {
        try {
            const response = await this.get('/enterprise/technical/daily_successful_logins_enterprise_version');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching daily enterprise version statistics:', error);
            return { success: false, error: error.message };
        }
    }

    // Technical / Database Metrics APIs
    async getDatabaseVacuumRequired() {
        try {
            const response = await this.get('/enterprise/technical/database/vacuum_required');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching database vacuum required:', error);
            return { success: false, error: error.message };
        }
    }

    async getDatabaseReplicationStatus() {
        try {
            const response = await this.get('/enterprise/technical/database/replication_status');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching database replication status:', error);
            return { success: false, error: error.message };
        }
    }

    async getDatabaseSlowQueries() {
        try {
            const response = await this.get('/enterprise/technical/database/slow_queries');
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching database slow queries:', error);
            return { success: false, error: error.message };
        }
    }

    // Ticket APIs
    async getTickets(organizationId) {
        console.log('Fetching tickets for organization ID:', organizationId);

        try {
            const queryParams = new URLSearchParams({
                ...(organizationId && { organization_id: organizationId })
            });
            const queryString = queryParams.toString();
            const response = await this.get(`/ticket${queryString ? `?${queryString}` : ''}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching tickets:', error);
            return { success: false, error: error.message };
        }
    }

    // Archived Records APIs (Customer Data)
    async getArchivedPlatformTeams(organizationId) {
        console.log('Fetching archived platform teams for organization ID:', organizationId);

        try {
            const response = await this.get(`/platform_team_archived/${organizationId}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching archived platform teams:', error);
            return { success: false, error: error.message };
        }
    }

    async getArchivedPlatformUserProfiles(organizationId) {
        console.log('Fetching archived platform user profiles for organization ID:', organizationId);

        try {
            const response = await this.get(`/platform_user_profile_archived/${organizationId}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching archived platform user profiles:', error);
            return { success: false, error: error.message };
        }
    }

    async getArchivedStorageLocations(organizationId) {
        console.log('Fetching archived storage locations for organization ID:', organizationId);

        try {
            const response = await this.get(`/storage_location_archived/${organizationId}`);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error fetching archived storage locations:', error);
            return { success: false, error: error.message };
        }
    }

    async restorePlatformUserProfile(id, ticketRef) {
        console.log('Restoring platform user profile ID:', id, 'ticket ref:', ticketRef);

        try {
            const response = await this.put(`/platform_user_profile_restore/${id}`, { ticket_ref: ticketRef });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error restoring platform user profile:', error);
            return { success: false, error: error.message };
        }
    }

    async restorePlatformTeam(id, ticketRef) {
        console.log('Restoring platform team ID:', id, 'ticket ref:', ticketRef);

        try {
            const response = await this.put(`/platform_team_restore/${id}`, { ticket_ref: ticketRef });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error restoring platform team:', error);
            return { success: false, error: error.message };
        }
    }

    async restoreStorageLocation(id, ticketRef) {
        console.log('Restoring storage location ID:', id, 'ticket ref:', ticketRef);

        try {
            const response = await this.put(`/storage_location_restore/${id}`, { ticket_ref: ticketRef });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error restoring storage location:', error);
            return { success: false, error: error.message };
        }
    }
}

// Create singleton instance
const apiService = new ApiService();
export default apiService;
